import {
  ChatOrderGuidesInfo,
  OrderGuideBatchUpdateSummary,
  OrderGuideUploadItem,
  OrderGuideUploadMode,
} from '@choco-admin/types';
import { getCurrencyDecimalPlaces } from '@choco-admin/utils';

import {
  OrderGuideUploadPageGetChatOrderGuidesQuery,
  OrderGuideUploadPageOrderGuideBatchUpdateMutation,
  OrderGuideV2BatchUpdateInput,
  OrderGuideV2BatchUpdateInputItem,
} from '../../../generated';
import { OrderGuideUploadRecord } from '../types';

/** CSV record → domain upload item (empty strings become undefined) */
export const recordToUploadItem = (
  record: OrderGuideUploadRecord
): OrderGuideUploadItem => ({
  productCode: record.productId || undefined,
  unit: record.unit,
  name: record.productName,
  categoryName: record.category || undefined,
  displayName: record.displayName || undefined,
  par: record.par || undefined,
  price:
    record.price && record.currency
      ? { amount: Number(record.price), currency: record.currency }
      : undefined,
});

const toBatchUpdateItem = (
  item: OrderGuideUploadItem
): OrderGuideV2BatchUpdateInputItem => ({
  productCode: item.productCode,
  unit: item.unit,
  name: item.name,
  orderGuideCategoryName: item.categoryName,
  displayItemName: item.displayName,
  par: item.par,
  myPriceV2: item.price
    ? {
        value: {
          amount: item.price.amount,
          currency: item.price.currency,
          decimalPlaces: getCurrencyDecimalPlaces(item.price.currency),
        },
      }
    : undefined,
});

export interface BatchUpdateParams {
  chatId: string;
  /** When set, targets this specific guide — takes precedence over chatId */
  orderGuideId: string | null;
  mode: OrderGuideUploadMode;
  dryRun: boolean;
  items: OrderGuideUploadItem[];
}

/**
 * Builds the mutation input. Sends orderGuideId whenever one is known —
 * with chatId alone the backend silently falls back to the fallback guide
 * of multi-guide chats, which is exactly what this feature avoids.
 */
export const buildBatchUpdateInput = (
  params: BatchUpdateParams
): OrderGuideV2BatchUpdateInput => ({
  ...(params.orderGuideId
    ? { orderGuideId: params.orderGuideId }
    : { chatId: params.chatId }),
  mode: params.mode,
  dryRun: params.dryRun,
  items: params.items.map(toBatchUpdateItem),
});

type ChatQueryResult = NonNullable<
  OrderGuideUploadPageGetChatOrderGuidesQuery['chat']
>;

/** Sentinel the backend stores for order guides that were never named */
const ORDER_GUIDE_NAME_PLACEHOLDER = '__name_placeholder__';

const cleanOrderGuideName = (name: string | null | undefined): string | null =>
  name && name !== ORDER_GUIDE_NAME_PLACEHOLDER ? name : null;

/** Generated chat query result → domain chat/order-guides info */
export const mapChatOrderGuides = (
  chat: ChatQueryResult
): ChatOrderGuidesInfo => ({
  chatId: chat.id,
  chatName: chat.chatName ?? null,
  restaurantName: chat.restaurantName ?? null,
  supplierName: chat.supplierEntity?.name ?? null,
  orderGuides: (chat.orderGuides ?? []).map((ref) => ({
    id: ref.node?.id ?? ref.id,
    name: cleanOrderGuideName(ref.node?.name),
    itemsCount: ref.node?.itemsCount ?? null,
  })),
});

type BatchUpdateResponse =
  OrderGuideUploadPageOrderGuideBatchUpdateMutation['orderGuideV2BatchUpdate'];

/** Generated mutation summary → domain summary */
export const mapBatchUpdateResponse = (
  response: BatchUpdateResponse
):
  | { ok: true; summary: OrderGuideBatchUpdateSummary }
  | { ok: false; errorMessage: string } => {
  if (response.__typename === 'OrderGuideV2BatchUpdateResult') {
    const { summary } = response;
    return {
      ok: true,
      summary: {
        existingItems: summary.existingOrderGuideItems,
        newItems: summary.newOrderGuideItems,
        updatedItems: summary.updatedOrderGuideItems,
        removedItems: summary.removedOrderGuideItems,
        createdCustomItems: summary.createdCustomVendorItems,
        errors: summary.errors.map((itemError) => ({
          index: itemError.index,
          reason: itemError.reason,
          errorCode: itemError.errorCode,
          productCode: itemError.item.productCode ?? null,
          unit: itemError.item.unit,
          name: itemError.item.name ?? null,
        })),
      },
    };
  }
  if (response.__typename === 'InputValidationFailure') {
    const fields = (response.invalidFields ?? [])
      .map((field) => `${field.fieldName}: ${field.reason}`)
      .join('; ');
    return {
      ok: false,
      errorMessage:
        response.message ?? (fields || 'The backend rejected the input.'),
    };
  }
  if (response.__typename === 'AuthorizationFailure') {
    return {
      ok: false,
      errorMessage:
        response.message ??
        'You are not authorized to update this order guide.',
    };
  }
  return { ok: false, errorMessage: 'Unexpected response from the backend.' };
};
